import {
  ACQUISITION_COOKIE,
  ACQUISITION_MAX_AGE,
  captureAcquisition,
  optionalAnalyticsAllowed,
  readAcquisitionRecord,
  type Acquisition,
} from "./analytics/acquisition"

let cached: { fields: Acquisition; capturedAt: number } | null = null

/** First observed acquisition, retained for seven days across OAuth and checkout.
 * Kept under the existing name so every browser log receives the same context.
 * Storage failures never affect navigation; raw query/referrer URLs are not kept.
 */
export function getSessionUtm(): Acquisition {
  if (typeof window === "undefined") return {}
  if (!optionalAnalyticsAllowed(window.navigator)) {
    cached = null
    try {
      window.sessionStorage.removeItem(ACQUISITION_COOKIE)
      document.cookie = `${ACQUISITION_COOKIE}=; Path=/; Max-Age=0; SameSite=Lax; Secure`
    } catch {
      /* Storage may be disabled. */
    }
    return {}
  }
  const now = Date.now()
  if (cached && now - cached.capturedAt < ACQUISITION_MAX_AGE * 1000) return cached.fields
  const candidates: string[] = []
  try {
    const cookie = document.cookie.match(/(?:^|;\s*)pcai_utm=([^;]+)/)?.[1]
    if (cookie) candidates.push(decodeURIComponent(cookie))
    const stored = window.sessionStorage.getItem(ACQUISITION_COOKIE)
    if (stored) candidates.push(stored)
  } catch {
    /* Best-effort persistence. */
  }
  for (const raw of candidates) {
    const fields = readAcquisitionRecord(raw, now)
    if (fields) {
      cached = { fields, capturedAt: JSON.parse(raw).captured_at }
      return fields
    }
  }
  let fields: Acquisition
  try {
    fields = captureAcquisition(window.location.href, document.referrer)
  } catch {
    return {}
  }
  cached = { fields, capturedAt: now }
  const encoded = JSON.stringify({ ...fields, captured_at: now })
  try {
    window.sessionStorage.setItem(ACQUISITION_COOKIE, encoded)
  } catch {
    /* Optional. */
  }
  try {
    document.cookie = `${ACQUISITION_COOKIE}=${encodeURIComponent(encoded)}; Path=/; Max-Age=${ACQUISITION_MAX_AGE}; SameSite=Lax; Secure`
  } catch {
    /* Optional. */
  }
  return fields
}
